import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type Location = Tables<"locations">;
export type LocationInsert = TablesInsert<"locations">;
export type LocationUpdate = TablesUpdate<"locations">;

export interface CityOption {
  city: string;
  locationIds: string[];
}

export const getAllLocations = async () => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .select("*")
      .order("is_default", { ascending: false })
      .order("name", { ascending: true });

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching locations:", error);
    return { success: false, error, data: null };
  }
};

/**
 * Récupère les villes distinctes avec leurs location IDs
 * Utile pour la sélection de ville quand aucun réalisateur n'est assigné
 */
export const getDistinctCities = async (): Promise<{ success: boolean; data: CityOption[] | null; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .select("id, city")
      .eq("is_active", true)
      .order("city", { ascending: true });

    if (error) throw error;

    // Grouper par ville
    const cityMap = new Map<string, string[]>();
    data?.forEach(loc => {
      if (loc.city) {
        const existing = cityMap.get(loc.city) || [];
        existing.push(loc.id);
        cityMap.set(loc.city, existing);
      }
    });

    const cities: CityOption[] = Array.from(cityMap.entries()).map(([city, locationIds]) => ({
      city,
      locationIds
    }));

    return { success: true, data: cities };
  } catch (error) {
    console.error("Error fetching distinct cities:", error);
    return { success: false, error, data: null };
  }
};

export const getDefaultLocation = async () => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .select("*")
      .eq("is_default", true)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching default location:", error);
    return { success: false, error, data: null };
  }
};

export const getLocationById = async (id: string) => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching location:", error);
    return { success: false, error, data: null };
  }
};

export const createLocation = async (location: LocationInsert) => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .insert(location)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error creating location:", error);
    return { success: false, error, data: null };
  }
};

export const updateLocation = async (id: string, updates: LocationUpdate) => {
  try {
    const { data, error } = await supabase
      .from("locations")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating location:", error);
    return { success: false, error, data: null };
  }
};

export const deleteLocation = async (id: string) => {
  try {
    const { error } = await supabase
      .from("locations")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting location:", error);
    return { success: false, error };
  }
};

export const setDefaultLocation = async (id: string) => {
  try {
    // D'abord, retirer le statut par défaut de toutes les localisations
    await supabase
      .from("locations")
      .update({ is_default: false })
      .neq("id", id);

    // Puis définir la nouvelle localisation par défaut
    const { data, error } = await supabase
      .from("locations")
      .update({ is_default: true })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error setting default location:", error);
    return { success: false, error, data: null };
  }
};
